import React, { useState, useEffect, useRef } from 'react';
import { profileService } from '../../services/api';
import {
  DatingProfile,
  UpdateProfileRequest,
  InterestCategory,
  PromptTemplate,
  UserPromptAnswer,
  BODY_TYPE_OPTIONS,
  EDUCATION_OPTIONS,
  SMOKING_OPTIONS,
  DRINKING_OPTIONS,
  EXERCISE_OPTIONS,
  WANTS_CHILDREN_OPTIONS,
  RELATIONSHIP_OPTIONS,
  GAMER_STATUS_OPTIONS,
  BodyType,
  Education,
  SmokingHabit,
  DrinkingHabit,
  Diet,
  ExerciseHabit,
  WantsChildren,
  RelationshipType,
  GamerStatus,
  AvailabilityWindow,
  parseAvailabilityWindows,
  serializeAvailabilityWindows,
} from '../../types/profile';
import { VisibilityControls } from './VisibilityControls';
import { AvailabilityPicker } from './AvailabilityPicker';
import { useProfileCards } from '../../context/ProfileCardContext';
import { useUser } from '../../context/UserContext';
import './DatingProfileEdit.css';

type ProfileTab = 'basics' | 'about' | 'lifestyle' | 'interests' | 'prompts' | 'photos' | 'gaming';

// V2 Amethyst WU-02 (R1): person-first order — identity/photo/human-substance
// (prompts, interests, looking-for+availability) lead; physical/background
// detail ("about") sits lower; gaming stays last, visually secondary.
const TAB_ORDER: ProfileTab[] = ['basics', 'photos', 'prompts', 'interests', 'lifestyle', 'about', 'gaming'];

interface DatingProfileEditProps {
  onClose: () => void;
  onSaved?: () => void;
}

export const DatingProfileEdit: React.FC<DatingProfileEditProps> = ({ onClose, onSaved }) => {
  const { invalidate } = useProfileCards();
  const { user } = useUser();
  const [profile, setProfile] = useState<DatingProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<ProfileTab>('basics');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Form state
  const [formData, setFormData] = useState<UpdateProfileRequest>({});

  // Interests
  const [categories, setCategories] = useState<InterestCategory[]>([]);
  const [selectedInterests, setSelectedInterests] = useState<number[]>([]);
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null);

  // Prompts
  const [promptTemplates, setPromptTemplates] = useState<PromptTemplate[]>([]);
  const [userPrompts, setUserPrompts] = useState<UserPromptAnswer[]>([]);
  const [editingPrompt, setEditingPrompt] = useState<PromptTemplate | null>(null);
  const [promptAnswer, setPromptAnswer] = useState('');

  // Photos
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      // Fetch categories and templates first
      const [cats, templates] = await Promise.all([
        profileService.getInterestCategories(),
        profileService.getPromptTemplates(),
      ]);
      setCategories(cats);
      setPromptTemplates(templates);

      // Fetch profile separately for better type narrowing
      const profileData = await profileService.getMyProfile();
      if (!profileData) {
        return;
      }

      setProfile(profileData);

      // Initialize form data
      setFormData({
        displayName: profileData.displayName,
        pronouns: profileData.pronouns,
        locationCity: profileData.locationCity,
        locationRegion: profileData.locationRegion,
        locationCountry: profileData.locationCountry,
        headline: profileData.headline,
        bio: profileData.bio,
        heightCm: profileData.heightCm,
        bodyType: profileData.bodyType,
        ethnicity: profileData.ethnicity,
        religion: profileData.religion,
        education: profileData.education,
        occupation: profileData.occupation,
        languages: profileData.languages,
        smoking: profileData.smoking,
        drinking: profileData.drinking,
        diet: profileData.diet,
        exercise: profileData.exercise,
        hasPets: profileData.hasPets,
        petTypes: profileData.petTypes,
        hasChildren: profileData.hasChildren,
        wantsChildren: profileData.wantsChildren,
        lookingFor: profileData.lookingFor,
        preferredGender: profileData.preferredGender,
        ageMin: profileData.ageMin,
        ageMax: profileData.ageMax,
        distanceMaxKm: profileData.distanceMaxKm,
        isGamer: profileData.isGamer,
        gameLookingForPartners: profileData.gameLookingForPartners,
        availabilityWindowsJson: profileData.availabilityWindowsJson,
        peekVisibility: profileData.peekVisibility,
      });

      // Initialize interests
      setSelectedInterests(profileData.interests.map((i) => i.interestId));

      // Initialize prompts
      setUserPrompts(profileData.prompts);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = <K extends keyof UpdateProfileRequest>(
    field: K,
    value: UpdateProfileRequest[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setMessage(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      await profileService.updateProfile(formData);
      setHasChanges(false);
      // Drop the owner's cached ProfileCard so every surface re-fetches the
      // edited identity on next render (AC-01.3 / AC-03.1). Self card is keyed
      // by user.id (see Sidebar's ProfileCardTrigger).
      if (user?.id) invalidate(user.id);
      setMessage({ type: 'success', text: 'Profile saved successfully!' });
      onSaved?.();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  // Interest handlers
  const toggleInterest = async (interestId: number) => {
    const newSelection = selectedInterests.includes(interestId)
      ? selectedInterests.filter((id) => id !== interestId)
      : [...selectedInterests, interestId];

    setSelectedInterests(newSelection);

    try {
      await profileService.updateInterests(newSelection);
      setMessage({ type: 'success', text: 'Interests updated' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to update interests' });
      setSelectedInterests(selectedInterests); // Revert
    }
  };

  // Prompt handlers
  const handleSavePrompt = async () => {
    if (!editingPrompt || !promptAnswer.trim()) return;

    try {
      const existing = userPrompts.find((p) => p.promptId === editingPrompt.id);

      if (existing) {
        await profileService.updatePromptAnswer(existing.id, promptAnswer);
        setUserPrompts((prev) =>
          prev.map((p) =>
            p.id === existing.id ? { ...p, answer: promptAnswer } : p
          )
        );
      } else {
        const result = await profileService.addPromptAnswer(editingPrompt.id, promptAnswer);
        if (result) {
          setUserPrompts((prev) => [
            ...prev,
            {
              id: result.id,
              promptId: result.promptId,
              promptText: editingPrompt.promptText,
              category: editingPrompt.category,
              answer: result.answer,
            },
          ]);
        }
      }

      setEditingPrompt(null);
      setPromptAnswer('');
      setMessage({ type: 'success', text: 'Prompt saved' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to save prompt' });
    }
  };

  const handleDeletePrompt = async (promptId: string) => {
    try {
      await profileService.deletePromptAnswer(promptId);
      setUserPrompts((prev) => prev.filter((p) => p.id !== promptId));
      setMessage({ type: 'success', text: 'Prompt deleted' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to delete prompt' });
    }
  };

  // Photo handlers
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const result = await profileService.uploadPhoto(file);
      if (profile && result) {
        setProfile({
          ...profile,
          photos: [...profile.photos, result],
        });
        setMessage({ type: 'success', text: 'Photo uploaded' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to upload photo' });
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    try {
      await profileService.deletePhoto(photoId);
      if (profile) {
        setProfile({
          ...profile,
          photos: profile.photos.filter((p) => p.id !== photoId),
        });
      }
      setMessage({ type: 'success', text: 'Photo deleted' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to delete photo' });
    }
  };

  const handleSetPrimaryPhoto = async (photoId: string) => {
    try {
      await profileService.setPhotoAsPrimary(photoId);
      if (profile) {
        setProfile({
          ...profile,
          photos: profile.photos.map((p) => ({
            ...p,
            isPrimary: p.id === photoId,
          })),
        });
      }
      setMessage({ type: 'success', text: 'Primary photo updated' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to set primary photo' });
    }
  };

  if (loading) {
    return (
      <div className="profile-edit-overlay">
        <div className="profile-edit-container">
          <div className="profile-edit-loading">Loading profile...</div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-edit-overlay">
        <div className="profile-edit-container">
          <div className="profile-edit-error">
            <p>Profile not found. Please complete registration first.</p>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-edit-overlay">
      <div className="profile-edit-container">
        <div className="profile-edit-header">
          <h1>Edit Profile</h1>
          <div className="profile-completion">
            <div className="completion-bar">
              <div
                className="completion-fill"
                style={{ width: `${profile.completionPercentage}%` }}
              />
            </div>
            <span>{profile.completionPercentage}% complete</span>
          </div>
          <button className="close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="profile-edit-tabs">
          {TAB_ORDER.map((tab) => (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="profile-edit-content">
          {/* Basics Tab */}
          {activeTab === 'basics' && (
            <div className="tab-panel">
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={formData.displayName || ''}
                  onChange={(e) => handleChange('displayName', e.target.value)}
                  maxLength={50}
                />
              </div>

              <div className="form-group">
                <label>Pronouns</label>
                <input
                  type="text"
                  value={formData.pronouns || ''}
                  onChange={(e) => handleChange('pronouns', e.target.value)}
                  placeholder="e.g., she/her, he/him, they/them"
                  maxLength={30}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.locationCity || ''}
                    onChange={(e) => handleChange('locationCity', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Region/State</label>
                  <input
                    type="text"
                    value={formData.locationRegion || ''}
                    onChange={(e) => handleChange('locationRegion', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    value={formData.locationCountry || ''}
                    onChange={(e) => handleChange('locationCountry', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Headline</label>
                <input
                  type="text"
                  value={formData.headline || ''}
                  onChange={(e) => handleChange('headline', e.target.value)}
                  placeholder="A catchy one-liner about you"
                  maxLength={150}
                />
              </div>

              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={formData.bio || ''}
                  onChange={(e) => handleChange('bio', e.target.value)}
                  placeholder="Tell people about yourself..."
                  rows={5}
                />
              </div>
            </div>
          )}

          {/* About Tab */}
          {activeTab === 'about' && (
            <div className="tab-panel">
              <div className="form-row">
                <div className="form-group">
                  <label>Height (cm)</label>
                  <input
                    type="number"
                    value={formData.heightCm || ''}
                    onChange={(e) => handleChange('heightCm', parseInt(e.target.value) || undefined)}
                    min={100}
                    max={250}
                  />
                </div>
                <div className="form-group">
                  <label>Body Type</label>
                  <select
                    value={formData.bodyType || ''}
                    onChange={(e) => handleChange('bodyType', e.target.value as BodyType)}
                  >
                    <option value="">Select...</option>
                    {BODY_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Ethnicity</label>
                <input
                  type="text"
                  value={formData.ethnicity || ''}
                  onChange={(e) => handleChange('ethnicity', e.target.value)}
                  placeholder="e.g., Caucasian, Asian, Mixed"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Occupation</label>
                  <input
                    type="text"
                    value={formData.occupation || ''}
                    onChange={(e) => handleChange('occupation', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Education</label>
                  <select
                    value={formData.education || ''}
                    onChange={(e) => handleChange('education', e.target.value as Education)}
                  >
                    <option value="">Select...</option>
                    {EDUCATION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Religion</label>
                  <input
                    type="text"
                    value={formData.religion || ''}
                    onChange={(e) => handleChange('religion', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Languages</label>
                  <input
                    type="text"
                    value={formData.languages || ''}
                    onChange={(e) => handleChange('languages', e.target.value)}
                    placeholder="e.g., English, Spanish"
                  />
                </div>
              </div>

              {/* Per-field visibility tiers (social.profile_card). Self-gates
                  on the flag — renders nothing until Phase A is enabled. */}
              <VisibilityControls />
            </div>
          )}

          {/* Lifestyle Tab */}
          {activeTab === 'lifestyle' && (
            <div className="tab-panel">
              <h3>Habits</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Smoking</label>
                  <select
                    value={formData.smoking || ''}
                    onChange={(e) => handleChange('smoking', e.target.value as SmokingHabit)}
                  >
                    <option value="">Select...</option>
                    {SMOKING_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Drinking</label>
                  <select
                    value={formData.drinking || ''}
                    onChange={(e) => handleChange('drinking', e.target.value as DrinkingHabit)}
                  >
                    <option value="">Select...</option>
                    {DRINKING_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Exercise</label>
                  <select
                    value={formData.exercise || ''}
                    onChange={(e) => handleChange('exercise', e.target.value as ExerciseHabit)}
                  >
                    <option value="">Select...</option>
                    {EXERCISE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Diet</label>
                  <select
                    value={formData.diet || ''}
                    onChange={(e) => handleChange('diet', e.target.value as Diet)}
                  >
                    <option value="">Select...</option>
                    <option value="none">No restrictions</option>
                    <option value="vegetarian">Vegetarian</option>
                    <option value="vegan">Vegan</option>
                    <option value="pescatarian">Pescatarian</option>
                    <option value="halal">Halal</option>
                    <option value="kosher">Kosher</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <h3>Family</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Has Children</label>
                  <select
                    value={formData.hasChildren === true ? 'yes' : formData.hasChildren === false ? 'no' : ''}
                    onChange={(e) =>
                      handleChange('hasChildren', e.target.value === 'yes' ? true : e.target.value === 'no' ? false : undefined)
                    }
                  >
                    <option value="">Prefer not to say</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Wants Children</label>
                  <select
                    value={formData.wantsChildren || ''}
                    onChange={(e) => handleChange('wantsChildren', e.target.value as WantsChildren)}
                  >
                    <option value="">Select...</option>
                    {WANTS_CHILDREN_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Has Pets</label>
                  <select
                    value={formData.hasPets === true ? 'yes' : formData.hasPets === false ? 'no' : ''}
                    onChange={(e) =>
                      handleChange('hasPets', e.target.value === 'yes' ? true : e.target.value === 'no' ? false : undefined)
                    }
                  >
                    <option value="">Prefer not to say</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                {formData.hasPets && (
                  <div className="form-group">
                    <label>Pet Types</label>
                    <input
                      type="text"
                      value={formData.petTypes || ''}
                      onChange={(e) => handleChange('petTypes', e.target.value)}
                      placeholder="e.g., Dog, Cat"
                    />
                  </div>
                )}
              </div>

              <h3>Looking For</h3>
              <div className="form-group">
                <label>Relationship Type</label>
                <select
                  value={formData.lookingFor || ''}
                  onChange={(e) => handleChange('lookingFor', e.target.value as RelationshipType)}
                >
                  {RELATIONSHIP_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Age Range</label>
                  <div className="age-range-inputs">
                    <input
                      type="number"
                      value={formData.ageMin || 18}
                      onChange={(e) => handleChange('ageMin', parseInt(e.target.value) || 18)}
                      min={18}
                      max={formData.ageMax || 99}
                    />
                    <span>to</span>
                    <input
                      type="number"
                      value={formData.ageMax || 99}
                      onChange={(e) => handleChange('ageMax', parseInt(e.target.value) || 99)}
                      min={formData.ageMin || 18}
                      max={99}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Max Distance (km)</label>
                  <input
                    type="number"
                    value={formData.distanceMaxKm || 50}
                    onChange={(e) => handleChange('distanceMaxKm', parseInt(e.target.value) || 50)}
                    min={1}
                    max={500}
                  />
                </div>
              </div>

              {/* Availability windows (V2 Amethyst WU-02, R2) — paired with
                  "Looking For" per the design doc's person-first order. */}
              <div className="form-group">
                <label>Availability</label>
                <AvailabilityPicker
                  value={parseAvailabilityWindows(formData.availabilityWindowsJson)}
                  onChange={(windows: AvailabilityWindow[]) =>
                    handleChange('availabilityWindowsJson', serializeAvailabilityWindows(windows))
                  }
                />
              </div>
            </div>
          )}

          {/* Interests Tab */}
          {activeTab === 'interests' && (
            <div className="tab-panel">
              <p className="tab-hint">
                Select interests to help find people with similar passions ({selectedInterests.length} selected)
              </p>

              <div className="interests-container">
                {categories.map((category) => (
                  <div key={category.id} className="interest-category">
                    <button
                      type="button"
                      className={`category-header ${expandedCategory === category.id ? 'expanded' : ''}`}
                      onClick={() =>
                        setExpandedCategory(expandedCategory === category.id ? null : category.id)
                      }
                    >
                      <span className="category-icon">{category.icon}</span>
                      <span className="category-name">{category.displayName}</span>
                      <span className="category-count">
                        {category.interests?.filter((i) => selectedInterests.includes(i.id)).length || 0}/
                        {category.interests?.length || 0}
                      </span>
                      <span className="chevron">{expandedCategory === category.id ? '▼' : '▶'}</span>
                    </button>

                    {expandedCategory === category.id && (
                      <div className="interest-list">
                        {category.interests?.map((interest) => (
                          <button
                            key={interest.id}
                            type="button"
                            className={`interest-item ${selectedInterests.includes(interest.id) ? 'selected' : ''}`}
                            onClick={() => toggleInterest(interest.id)}
                          >
                            {interest.displayName}
                            {selectedInterests.includes(interest.id) && <span className="check">&#10003;</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Prompts Tab */}
          {activeTab === 'prompts' && (
            <div className="tab-panel">
              <p className="tab-hint">Answer prompts to help others start conversations with you</p>

              {editingPrompt ? (
                <div className="prompt-editor">
                  <div className="prompt-question">{editingPrompt.promptText}</div>
                  <textarea
                    value={promptAnswer}
                    onChange={(e) => setPromptAnswer(e.target.value)}
                    placeholder="Your answer..."
                    rows={4}
                    maxLength={500}
                  />
                  <div className="prompt-editor-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        setEditingPrompt(null);
                        setPromptAnswer('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleSavePrompt}
                      disabled={!promptAnswer.trim()}
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {userPrompts.length > 0 && (
                    <div className="answered-prompts">
                      <h4>Your Answers</h4>
                      {userPrompts.map((prompt) => (
                        <div key={prompt.id} className="answered-prompt">
                          <div className="prompt-text">{prompt.promptText}</div>
                          <div className="prompt-answer">{prompt.answer}</div>
                          <div className="prompt-actions">
                            <button
                              type="button"
                              onClick={() => {
                                const template = promptTemplates.find((t) => t.id === prompt.promptId);
                                if (template) {
                                  setEditingPrompt(template);
                                  setPromptAnswer(prompt.answer);
                                }
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="danger"
                              onClick={() => handleDeletePrompt(prompt.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="available-prompts">
                    <h4>Add a Prompt</h4>
                    <div className="prompt-list">
                      {promptTemplates
                        .filter((t) => !userPrompts.find((p) => p.promptId === t.id))
                        .map((template) => (
                          <button
                            key={template.id}
                            type="button"
                            className="prompt-item"
                            onClick={() => {
                              setEditingPrompt(template);
                              setPromptAnswer('');
                            }}
                          >
                            {template.promptText}
                          </button>
                        ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Photos Tab */}
          {activeTab === 'photos' && (
            <div className="tab-panel">
              <p className="tab-hint">
                Add up to 9 photos. The first photo is your primary photo shown to others.
              </p>

              <div className="photos-grid">
                {profile.photos.map((photo) => (
                  <div
                    key={photo.id}
                    className={`photo-item ${photo.isPrimary ? 'primary' : ''}`}
                  >
                    <img src={photo.mediumUrl || photo.thumbnailUrl} alt="" />
                    {photo.isPrimary && <span className="primary-badge">Primary</span>}
                    <div className="photo-actions">
                      {!photo.isPrimary && (
                        <button
                          type="button"
                          onClick={() => handleSetPrimaryPhoto(photo.id)}
                        >
                          Set Primary
                        </button>
                      )}
                      <button
                        type="button"
                        className="danger"
                        onClick={() => handleDeletePhoto(photo.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}

                {profile.photos.length < 9 && (
                  <div className="photo-upload-slot">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhoto}
                      style={{ display: 'none' }}
                    />
                    <button
                      type="button"
                      className="upload-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                    >
                      {uploadingPhoto ? 'Uploading...' : '+ Add Photo'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Gaming Tab */}
          {activeTab === 'gaming' && (
            <div className="tab-panel">
              <div className="form-group">
                <label>Are you a gamer?</label>
                <div className="option-buttons">
                  {GAMER_STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`option-btn ${formData.isGamer === opt.value ? 'selected' : ''}`}
                      onClick={() => handleChange('isGamer', opt.value as GamerStatus)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {formData.isGamer !== 'no' && (
                <div className="form-group">
                  <label>Looking for gaming partners?</label>
                  <div className="option-buttons">
                    <button
                      type="button"
                      className={`option-btn ${formData.gameLookingForPartners === true ? 'selected' : ''}`}
                      onClick={() => handleChange('gameLookingForPartners', true)}
                    >
                      Yes, I'd love to find gaming buddies!
                    </button>
                    <button
                      type="button"
                      className={`option-btn ${formData.gameLookingForPartners === false ? 'selected' : ''}`}
                      onClick={() => handleChange('gameLookingForPartners', false)}
                    >
                      No, just here for dating
                    </button>
                  </div>
                </div>
              )}

              <div className="gaming-info">
                <p>
                  GameDate lets you meet people in virtual spaces! If you're a gamer, you can meet
                  matches in our 3D environments and have fun together.
                </p>
              </div>
            </div>
          )}
        </div>

        {message && (
          <div className={`profile-edit-message ${message.type}`}>{message.text}</div>
        )}

        <div className="profile-edit-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {hasChanges ? 'Cancel' : 'Close'}
          </button>
          {['basics', 'about', 'lifestyle', 'gaming'].includes(activeTab) && (
            <button
              type="button"
              className="btn-primary"
              onClick={handleSave}
              disabled={saving || !hasChanges}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DatingProfileEdit;
